/* AdminSupportConsole — Phase S-3 (Feb 2026)
 *
 * Curated single-screen support console with 8 guided action cards.
 * Each card opens a 3-step wizard:
 *
 *   Step 1: Target — search a user / invoice / card / notification
 *   Step 2: Reason — type ≥3 char audit reason + optional extras
 *           (amount for wallet / payout)
 *   Step 3: Impact preview — server-rendered effects + warnings
 *           then either "Apply" or "Cancel"
 *
 * Scope-gated: cards the admin's role cannot execute render with a
 * lock icon and a friendly "Ask Engineering" tooltip instead of the
 * normal CTA. Defense-in-depth — server re-validates on apply.
 *
 * Recent actions panel at the bottom shows the last 50 console actions
 * (filterable to "mine only") from `admin_overrides` where
 * metadata.via_support_console=true.
 */
import { useEffect, useMemo, useState } from "react";
import {
  ReceiptText, PlusCircle, Banknote, Snowflake, UserX, LogOut,
  KeyRound, BellRing, Lock, Loader2, Search, ShieldCheck,
  AlertTriangle, ArrowRight, ArrowLeft, CheckCircle2, X,
  ChevronRight, Sparkles, Filter, RefreshCw, History,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";


// ─── Static maps ───────────────────────────────────────────────────────
const ICON_MAP = {
  ReceiptText, PlusCircle, Banknote, Snowflake, UserX, LogOut,
  KeyRound, BellRing,
};

const COLOR_TINTS = {
  rose:    "from-rose-500/15 via-rose-500/5 to-transparent border-rose-200 dark:border-rose-900/50",
  emerald: "from-emerald-500/15 via-emerald-500/5 to-transparent border-emerald-200 dark:border-emerald-900/50",
  sky:     "from-sky-500/15 via-sky-500/5 to-transparent border-sky-200 dark:border-sky-900/50",
  violet:  "from-violet-500/15 via-violet-500/5 to-transparent border-violet-200 dark:border-violet-900/50",
  amber:   "from-amber-500/15 via-amber-500/5 to-transparent border-amber-200 dark:border-amber-900/50",
  indigo:  "from-indigo-500/15 via-indigo-500/5 to-transparent border-indigo-200 dark:border-indigo-900/50",
  teal:    "from-teal-500/15 via-teal-500/5 to-transparent border-teal-200 dark:border-teal-900/50",
};

const ICON_BG = {
  rose: "bg-rose-500/10 text-rose-600 dark:text-rose-400",
  emerald: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  sky: "bg-sky-500/10 text-sky-600 dark:text-sky-400",
  violet: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
  amber: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  indigo: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400",
  teal: "bg-teal-500/10 text-teal-600 dark:text-teal-400",
};

const SEVERITY_CHIP = {
  high:   "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300",
  medium: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  low:    "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
};


function fmtUSD(n) {
  if (n == null) return "—";
  return `$${Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    if (diff < 60_000) return "just now";
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}


// ─── Card grid item ────────────────────────────────────────────────────
function SupportCard({ card, onClick }) {
  const Icon = ICON_MAP[card.icon] || Sparkles;
  const tint = COLOR_TINTS[card.color] || COLOR_TINTS.sky;
  const iconBg = ICON_BG[card.color] || ICON_BG.sky;
  const sevChip = SEVERITY_CHIP[card.severity] || SEVERITY_CHIP.low;

  const locked = !card.allowed;

  return (
    <button
      type="button"
      data-testid={`support-card-${card.id}`}
      data-allowed={card.allowed}
      onClick={() => !locked && onClick(card)}
      disabled={locked}
      className={`group relative flex flex-col gap-3 rounded-xl border bg-gradient-to-br ${tint}
        p-5 text-left transition
        ${locked ? "opacity-60 cursor-not-allowed" : "hover:shadow-md hover:scale-[1.01]"}
      `}
    >
      <div className="flex items-start justify-between gap-3">
        <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${iconBg}`}>
          {locked ? <Lock className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
        </div>
        <span className={`text-[10px] font-semibold tracking-wider uppercase rounded-full px-2 py-0.5 ${sevChip}`}>
          {card.severity}
        </span>
      </div>
      <div>
        <div className="text-sm font-semibold text-slate-900 dark:text-slate-50 leading-tight">
          {card.label}
        </div>
        <div className="mt-1 text-xs text-slate-600 dark:text-slate-400 leading-snug">
          {card.description}
        </div>
      </div>
      <div className="flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-500 pt-1 border-t border-slate-200/50 dark:border-slate-800/50">
        <span className="capitalize">{card.category} action</span>
        {locked ? (
          <span className="flex items-center gap-1">
            <Lock className="h-3 w-3" /> Locked
          </span>
        ) : (
          <span className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
            Start <ChevronRight className="h-3 w-3" />
          </span>
        )}
      </div>
    </button>
  );
}


// ─── 3-step Wizard Dialog ──────────────────────────────────────────────
function WizardDialog({ card, open, onClose, onApplied }) {
  const [step, setStep] = useState(1);
  const [q, setQ] = useState("");
  const [searchKind, setSearchKind] = useState(card?.target_kind || "user");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);  // entity
  const [extras, setExtras] = useState({});    // {amount, memo, ...}
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [applying, setApplying] = useState(false);

  // Reset on open
  useEffect(() => {
    if (open && card) {
      setStep(1); setQ(""); setSearchKind(card.target_kind);
      setResults([]); setPicked(null); setExtras({});
      setReason(""); setPreview(null);
    }
  }, [open, card?.id]);

  if (!card) return null;

  // ── Search ──
  const doSearch = async () => {
    if (q.trim().length < 2) return;
    setSearching(true);
    try {
      const { data } = await api.get(`/admin/support/search`, {
        params: { q: q.trim(), kind: searchKind, limit: 15 },
      });
      setResults(data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally {
      setSearching(false);
    }
  };

  // ── Preview ──
  const fetchPreview = async (target, currentExtras) => {
    if (!target?.id) return;
    setLoadingPreview(true);
    try {
      const params = {};
      if (currentExtras.amount) params.amount = currentExtras.amount;
      const { data } = await api.get(
        `/admin/support/preview/${card.id}/${target.id}`,
        { params },
      );
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
        || e?.response?.data?.detail
        || "Preview failed");
      setPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const onPick = (entity) => {
    setPicked(entity);
    setStep(2);
  };

  const advanceToPreview = async () => {
    if (reason.trim().length < 3) {
      toast.error("Reason must be at least 3 characters.");
      return;
    }
    // Validate required extras
    for (const ex of card.extras || []) {
      if (ex.required && !extras[ex.key]) {
        toast.error(`${ex.label} is required.`);
        return;
      }
    }
    await fetchPreview(picked, extras);
    setStep(3);
  };

  const apply = async () => {
    setApplying(true);
    try {
      const payload = { reason: reason.trim() };
      if (extras.amount) payload.amount = Number(extras.amount);
      if (extras.memo) payload.memo = extras.memo;
      const { data } = await api.post(
        `/admin/support/apply/${card.id}/${picked.id}`,
        payload,
      );
      toast.success(`Done · audit-logged (override ${String(data.override_id || "").slice(0, 8)}).`);
      onApplied && onApplied(data);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Apply failed"));
    } finally {
      setApplying(false);
    }
  };

  const Icon = ICON_MAP[card.icon] || Sparkles;
  const iconBg = ICON_BG[card.color] || ICON_BG.sky;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="support-wizard-dialog"
        className="max-w-2xl"
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${iconBg}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle>{card.label}</DialogTitle>
              <DialogDescription className="text-xs">
                Step {step} of 3 · {card.description}
              </DialogDescription>
            </div>
          </div>
          {/* Stepper */}
          <div className="flex items-center gap-2 mt-3" data-testid="wizard-stepper">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`h-1.5 flex-1 rounded-full transition ${
                  s <= step
                    ? "bg-slate-900 dark:bg-slate-100"
                    : "bg-slate-200 dark:bg-slate-800"
                }`}
              />
            ))}
          </div>
        </DialogHeader>

        {/* ── Step 1: Target ── */}
        {step === 1 && (
          <div className="space-y-3" data-testid="wizard-step-1">
            <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
              {card.target_label}
            </label>
            <div className="flex gap-2">
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && doSearch()}
                placeholder="Type to search…"
                data-testid="wizard-search-input"
                autoFocus
              />
              <Button
                onClick={doSearch}
                disabled={searching || q.trim().length < 2}
                data-testid="wizard-search-btn"
              >
                {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>

            <div className="max-h-72 overflow-y-auto space-y-1.5 pr-1" data-testid="wizard-search-results">
              {!searching && results.length === 0 && q.trim().length >= 2 && (
                <div className="text-xs text-slate-500 text-center py-6">No matches.</div>
              )}
              {results.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => onPick(r)}
                  className="w-full flex items-center justify-between gap-2 rounded-lg border p-3 text-left
                             hover:bg-slate-50 dark:hover:bg-slate-900 transition"
                  data-testid={`wizard-result-${r.id}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">
                      {r.email || r.code || r.title || r.label || r.id?.slice(0, 8)}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {r.name || r.company || r.kind || r.user_id?.slice(0, 8) || r.id}
                      {r.role && <span className="ml-2 uppercase text-[10px] tracking-wider">{r.role}</span>}
                      {r.status && <span className="ml-2 text-[10px]">{r.status}</span>}
                      {r.amount != null && <span className="ml-2">{fmtUSD(r.amount)}</span>}
                      {r.last4 && <span className="ml-2">···{r.last4}</span>}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
                </button>
              ))}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose} data-testid="wizard-cancel-btn">Cancel</Button>
            </DialogFooter>
          </div>
        )}

        {/* ── Step 2: Reason + extras ── */}
        {step === 2 && (
          <div className="space-y-4" data-testid="wizard-step-2">
            <div className="rounded-lg border bg-slate-50 dark:bg-slate-900 p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Target</div>
              <div className="text-sm font-medium mt-0.5">
                {picked?.email || picked?.code || picked?.title || picked?.id}
              </div>
              <div className="text-xs text-slate-500">
                {picked?.name || picked?.company || picked?.kind || picked?.id?.slice(0, 12)}
              </div>
            </div>

            {(card.extras || []).map((ex) => (
              <div key={ex.key}>
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1 block">
                  {ex.label} {ex.required && <span className="text-rose-500">*</span>}
                </label>
                {ex.type === "number" ? (
                  <Input
                    type="number"
                    min={ex.min}
                    max={ex.max}
                    value={extras[ex.key] || ""}
                    onChange={(e) => setExtras({ ...extras, [ex.key]: e.target.value })}
                    data-testid={`wizard-extra-${ex.key}`}
                  />
                ) : (
                  <Input
                    type="text"
                    maxLength={ex.max || 200}
                    value={extras[ex.key] || ""}
                    onChange={(e) => setExtras({ ...extras, [ex.key]: e.target.value })}
                    data-testid={`wizard-extra-${ex.key}`}
                  />
                )}
              </div>
            ))}

            <div>
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1 block">
                Audit reason <span className="text-rose-500">*</span>
              </label>
              <Textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="≥3 characters · permanently stored in the audit chain"
                data-testid="wizard-reason"
              />
              <div className="text-[11px] text-slate-500 mt-1">
                Hash-chained to `admin_overrides` · visible to compliance + the user being affected.
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setStep(1)} data-testid="wizard-back-btn">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button
                onClick={advanceToPreview}
                disabled={reason.trim().length < 3}
                data-testid="wizard-next-btn"
              >
                Preview impact <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* ── Step 3: Impact preview ── */}
        {step === 3 && (
          <div className="space-y-3" data-testid="wizard-step-3">
            {loadingPreview && (
              <div className="flex items-center justify-center py-12 text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Building preview…
              </div>
            )}

            {!loadingPreview && preview && (
              <>
                {preview.impact_usd != null && (
                  <div className="rounded-lg border bg-gradient-to-br from-amber-500/10 to-amber-500/5 border-amber-200 p-3"
                       data-testid="wizard-preview-impact">
                    <div className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-300 font-semibold">
                      Financial impact
                    </div>
                    <div className="text-2xl font-bold text-amber-900 dark:text-amber-200 mt-0.5 tabular-nums">
                      {fmtUSD(preview.impact_usd)}
                    </div>
                  </div>
                )}

                <div className="rounded-lg border bg-slate-50 dark:bg-slate-900 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                    What will happen
                  </div>
                  <ul className="space-y-1.5">
                    {(preview.effects || []).map((eff, i) => (
                      <li key={i} className="text-sm flex gap-2 items-start" data-testid={`wizard-effect-${i}`}>
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                        <span>{eff}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {(preview.warnings || []).length > 0 && (
                  <div className="rounded-lg border border-rose-200 dark:border-rose-900/50
                                  bg-rose-50 dark:bg-rose-950/30 p-3"
                       data-testid="wizard-preview-warnings">
                    <div className="text-[10px] uppercase tracking-wider text-rose-700 dark:text-rose-300 font-semibold mb-2 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Warnings
                    </div>
                    <ul className="space-y-1.5">
                      {preview.warnings.map((w, i) => (
                        <li key={i} className="text-sm text-rose-700 dark:text-rose-300">{w}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {!preview.can_apply && (
                  <div className="rounded-lg border border-slate-300 bg-slate-100 dark:bg-slate-900 p-3 text-sm"
                       data-testid="wizard-cannot-apply">
                    <div className="font-semibold text-slate-700 dark:text-slate-300 mb-0.5">
                      Cannot apply
                    </div>
                    <div className="text-slate-600 dark:text-slate-400">
                      {preview.reason_message || "Preconditions not met."}
                    </div>
                  </div>
                )}
              </>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setStep(2)} data-testid="wizard-back-btn-step3">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button
                onClick={apply}
                disabled={applying || !preview?.can_apply}
                data-testid="wizard-apply-btn"
              >
                {applying ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                Apply action
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ─── Recent panel ──────────────────────────────────────────────────────
function RecentPanel({ refreshKey }) {
  const [rows, setRows] = useState([]);
  const [byAction, setByAction] = useState({});
  const [loading, setLoading] = useState(true);
  const [mineOnly, setMineOnly] = useState(false);
  const [sinceDays, setSinceDays] = useState(7);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/support/recent", {
        params: { mine_only: mineOnly, since_days: sinceDays, limit: 50 },
      });
      setRows(data.rows || []);
      setByAction(data.by_action || {});
    } catch (e) {
      // silent — non-fatal
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [mineOnly, sinceDays, refreshKey]);

  return (
    <div className="rounded-2xl border bg-white dark:bg-slate-950 p-5" data-testid="support-recent-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-slate-500" />
          <h3 className="text-sm font-semibold">Recent console actions</h3>
          <Badge variant="outline" className="text-[10px]">{rows.length}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={sinceDays}
            onChange={(e) => setSinceDays(Number(e.target.value))}
            data-testid="recent-since-filter"
            className="text-xs border rounded px-2 py-1 bg-white dark:bg-slate-950"
          >
            <option value={1}>24h</option>
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
          </select>
          <label className="text-xs flex items-center gap-1 cursor-pointer">
            <input
              type="checkbox"
              checked={mineOnly}
              onChange={(e) => setMineOnly(e.target.checked)}
              data-testid="recent-mine-only"
            />
            Mine only
          </label>
          <Button size="sm" variant="ghost" onClick={load} data-testid="recent-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* by_action chip strip */}
      {Object.keys(byAction).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="recent-by-action">
          {Object.entries(byAction).map(([a, n]) => (
            <Badge key={a} variant="secondary" className="text-[10px] tabular-nums">
              {a} <span className="ml-1 font-semibold">{n}</span>
            </Badge>
          ))}
        </div>
      )}

      {loading && rows.length === 0 && (
        <div className="text-xs text-slate-500 text-center py-8">Loading…</div>
      )}
      {!loading && rows.length === 0 && (
        <div className="text-xs text-slate-500 text-center py-8">No console actions in the selected window.</div>
      )}

      <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1" data-testid="recent-rows">
        {rows.map((r) => (
          <div
            key={r.id}
            className="flex items-center justify-between gap-3 rounded-lg border p-2.5 text-xs"
            data-testid={`recent-row-${r.id}`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <Badge
                variant="outline"
                className="font-mono text-[10px] tabular-nums shrink-0"
              >
                #{r.chain_position}
              </Badge>
              <div className="min-w-0">
                <div className="font-medium truncate">{r.action}</div>
                <div className="text-slate-500 truncate">
                  by {r.actor_email || r.actor_id?.slice(0, 8)} · {r.reason?.slice(0, 80)}
                </div>
              </div>
            </div>
            <div className="text-right shrink-0">
              {r.impact_usd > 0 && (
                <div className="font-semibold tabular-nums text-amber-700">{fmtUSD(r.impact_usd)}</div>
              )}
              <div className="text-slate-500 text-[10px]">{fmtTime(r.created_at)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ─── Page ──────────────────────────────────────────────────────────────
export default function AdminSupportConsole() {
  const [cards, setCards] = useState([]);
  const [role, setRole] = useState(null);
  const [allowedCount, setAllowedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeCard, setActiveCard] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [categoryFilter, setCategoryFilter] = useState("all");

  const loadCards = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/support/cards");
      setCards(data.cards || []);
      setRole(data.role);
      setAllowedCount(data.allowed);
      setTotalCount(data.total);
    } catch (e) {
      toast.error("Could not load support cards.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCards(); }, []);

  const openCard = (card) => {
    setActiveCard(card);
    setWizardOpen(true);
  };

  const onApplied = () => {
    setRefreshKey((k) => k + 1);
  };

  const filteredCards = useMemo(() => {
    if (categoryFilter === "all") return cards;
    return cards.filter((c) => c.category === categoryFilter);
  }, [cards, categoryFilter]);

  const categories = useMemo(() => {
    const set = new Set(cards.map((c) => c.category));
    return ["all", ...Array.from(set)];
  }, [cards]);

  return (
    <div className="space-y-6 p-6" data-testid="admin-support-console">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="h-5 w-5 text-slate-700 dark:text-slate-300" />
          <h1 className="text-2xl font-bold">Support Console</h1>
        </div>
        <p className="text-sm text-slate-500">
          Guided 3-step wizards for the 8 most common support operations.
          Every action is scope-gated, impact-previewed, and hash-chained.
        </p>
      </div>

      {/* Role summary */}
      <div className="flex flex-wrap items-center gap-3" data-testid="support-role-summary">
        <Badge variant="secondary" className="text-xs">
          Role: <span className="ml-1 font-semibold capitalize">{role || "—"}</span>
        </Badge>
        <Badge variant="outline" className="text-xs">
          <ShieldCheck className="h-3 w-3 mr-1" />
          {allowedCount} of {totalCount} cards available to your role
        </Badge>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-1.5" data-testid="support-category-filter">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setCategoryFilter(c)}
            data-testid={`category-${c}`}
            className={`text-xs px-3 py-1.5 rounded-full border capitalize transition ${
              categoryFilter === c
                ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
                : "hover:bg-slate-50 dark:hover:bg-slate-900"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Cards grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading cards…
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
             data-testid="support-cards-grid">
          {filteredCards.map((card) => (
            <SupportCard key={card.id} card={card} onClick={openCard} />
          ))}
        </div>
      )}

      {/* Recent panel */}
      <RecentPanel refreshKey={refreshKey} />

      {/* Wizard */}
      <WizardDialog
        card={activeCard}
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onApplied={onApplied}
      />
    </div>
  );
}
